import { Button, Modal, Segmented } from 'antd';
import i18next from 'i18next';
import React, { useState } from 'react';
import Marmotte, { MarmotteQuality } from './Marmotte';
import { DTFItem } from './packing';
import { fmtCm, QualityInfo } from './quality';
import { ThinFixMode } from './thinfix';
import { canUpscale } from './upscale';
import UpscaleAction from './UpscaleAction';

interface IProps {
	item: DTFItem | null;
	quality?: QualityInfo;
	minDpi: number;
	thin: boolean;
	lowDpi?: number;
	upscaling: boolean;
	fixingThin: boolean;
	onUpscale: (id: string) => void;
	onFixThin: (id: string, mode: ThinFixMode, garmentColor?: string) => void;
	onBgEdit: (id: string) => void;
	onClose: () => void;
}

export const rangeText = (q: QualityInfo, kind = 'raster'): { bad: boolean; txt: string } => {
	const min = q.minCm !== null && q.minCm !== Infinity ? q.minCm : null;
	// traits trop fins À TOUTES les largeurs : l'IA n'y change rien (la géométrie
	// des traits reste la même) — la bonne action est d'épaissir ou de poser un fond
	if (q.minCm === Infinity) return { bad: true, txt: i18next.t('dtf.plage-aucune-traits') };
	if (min !== null && q.maxCm !== null && min > q.maxCm) {
		// conflit largeur : il faudrait ≥ min cm pour les traits, mais les DPI plafonnent
		// à max cm — l'IA relève ce plafond, sinon épaissir/fichier plus grand
		const iaUtile = kind === 'raster' && canUpscale(q.pxW, q.pxH);
		return { bad: true, txt: i18next.t(iaUtile ? 'dtf.plage-aucune' : 'dtf.plage-aucune-max') };
	}
	if (min !== null && q.maxCm !== null)
		return { bad: false, txt: i18next.t('dtf.plage-ok', { min: fmtCm(min), max: fmtCm(q.maxCm) }) };
	if (min !== null) return { bad: false, txt: i18next.t('dtf.plage-des', { min: fmtCm(min) }) };
	if (q.maxCm !== null) return { bad: false, txt: i18next.t('dtf.plage-jusqua', { max: fmtCm(q.maxCm) }) };
	return { bad: false, txt: i18next.t('dtf.plage-toutes') };
};

const ZOOMS = [50, 100, 200, 400];

const QualityModal: React.FC<IProps> = ({
	item,
	quality,
	minDpi,
	thin,
	lowDpi,
	upscaling,
	fixingThin,
	onUpscale,
	onFixThin,
	onBgEdit,
	onClose,
}) => {
	const [zoom, setZoom] = useState(100);
	const [garmentColor, setGarmentColor] = useState('#ffffff');
	if (!item) return null;
	// fiche périmée (logo remplacé par upscale/correction) : repasser en « analyse en cours »
	const q = quality && quality.src === item.src ? quality : undefined;
	const range = q ? rangeText(q, item.kind) : null;
	const dpiNow = q && item.kind === 'raster' ? Math.round(q.pxW / (item.widthCm / 2.54)) : null;

	return (
		<Modal centered
			open
			title={`🔍 ${i18next.t('dtf.qualite-titre')} — ${item.name}`}
			footer={null}
			width={900}
			className="dtf-quality-modal"
			onCancel={onClose}
		>
			<div className="dtf-quality-body">
				<div className="dtf-quality-viewer-col">
					<div className="dtf-quality-zoom-row">
						<Segmented
							size="small"
							value={zoom}
							onChange={v => setZoom(Number(v))}
							options={ZOOMS.map(z => ({ value: z, label: `${z} %` }))}
						/>
						<span className="dtf-quality-zoom-hint">{i18next.t('dtf.qualite-zoom-aide')}</span>
					</div>
					<div className="dtf-quality-viewer">
						<img
							src={item.src}
							alt=""
							className="dtf-quality-img"
							style={q ? { width: `${Math.round((q.pxW * zoom) / 100)}px` } : undefined}
						/>
						{item.improved && <span className="dtf-improved-badge">✨ {i18next.t('dtf.image-amelioree')}</span>}
					</div>
					{item.improved && (
						<Marmotte pose="magic" tone="ok" size={44} className="dtf-marmotte-ia" title={i18next.t('dtf.mmt-ia-t') as string}>
							{i18next.t('dtf.mmt-ia')}
						</Marmotte>
					)}
				</div>
				<div className="dtf-quality-info">
					<MarmotteQuality item={item} q={q} minDpi={minDpi} thin={thin} size={56} />
					{!q && <p>{i18next.t('dtf.qualite-analyse')}</p>}
					{q && (
						<>
							{item.kind === 'raster' ? (
								<p className="dtf-quality-dims">{i18next.t('dtf.qualite-dims', { w: q.pxW, h: q.pxH })}</p>
							) : (
								<p className="dtf-quality-dims">{i18next.t('dtf.qualite-vectoriel')}</p>
							)}
							{dpiNow !== null && (
								<p className={lowDpi !== undefined ? 'dtf-quality-bad-line' : ''}>
									{i18next.t('dtf.qualite-dpi-actuel', { w: fmtCm(item.widthCm), dpi: dpiNow, min: minDpi })}
								</p>
							)}
							{range && (
								<p className={`dtf-quality-range-line${range.bad ? ' dtf-quality-bad-line' : ''}`}>📐 {range.txt}</p>
							)}
							{q.minCm !== null && q.minCm !== Infinity && (
								<p className="dtf-quality-explain">{i18next.t('dtf.plage-min-explique', { min: fmtCm(q.minCm) })}</p>
							)}
							{q.maxCm !== null && (
								<p className="dtf-quality-explain">
									{i18next.t('dtf.plage-max-explique', { max: fmtCm(q.maxCm), dpi: minDpi })}
								</p>
							)}
							<div className="dtf-quality-actions">
								{item.kind !== 'svg' && (
									<Button className="dtf-bg-btn" onClick={() => onBgEdit(item.id)} block>
										✂️ {i18next.t('dtf.detourage')}
									</Button>
								)}
								{item.kind === 'raster' && (
									<UpscaleAction
										q={q}
										widthCm={item.widthCm}
										minDpi={minDpi}
										loading={upscaling}
										onClick={() => onUpscale(item.id)}
									/>
								)}
								{(thin || (range && range.bad)) && (
									<>
										<Button
											className="dtf-thinfix-btn dtf-thinfix-thicken"
											loading={fixingThin}
											onClick={() => onFixThin(item.id, 'thicken')}
											block
										>
											🖌 {i18next.t('dtf.epaissir')}
										</Button>
										<div className="dtf-quality-backing-row">
											<Button
												className="dtf-thinfix-btn dtf-thinfix-backing"
												loading={fixingThin}
												onClick={() => onFixThin(item.id, 'backing', garmentColor)}
											>
												👕 {i18next.t('dtf.fond-traits')}
											</Button>
											<label className="dtf-garment-pick" title={i18next.t('dtf.fond-couleur') as string}>
												<span>{i18next.t('dtf.fond-couleur-court')}</span>
												<input
													type="color"
													className="dtf-garment-color"
													value={garmentColor}
													onChange={e => setGarmentColor(e.target.value)}
												/>
											</label>
										</div>
									</>
								)}
							</div>
						</>
					)}
					<Button onClick={onClose} block className="dtf-quality-close">
						{i18next.t('dtf.fermer')}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default QualityModal;
